import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { Location } from "history";
import { useLocation } from "react-router";
import { HashRouter } from "react-router-dom";

import { EntityType } from "@mapmycustomers/shared/enum";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import { dealAnalytics } from "@app/component/preview/analytics";
import useHideOnDelete from "@app/component/preview/util/useHideOnDelete";
import Path from "@app/enum/Path";
import { addUserRecent } from "@app/store/globalSearch/actions";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import eventEmitter from "@app/util/eventEmitter";

import DealRecordPane, { DealRecordPaneProps } from "./DealRecordPane";

const PREVIEW_PATH_REGEX = new RegExp(`^${Path.DEAL}/\\d+$`);

interface Props extends Omit<DealRecordPaneProps, "onShouldShowPrompt"> {
  addUserRecent: typeof addUserRecent;
  onDiscardChanges?: (changesDiscarded: boolean) => void;
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
}

const RoutedDealRecordPane: React.FC<Props> = (props) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isPathnameChanged, setPathnameChanged] = useBoolean();
  const { pathname } = useLocation();
  const shouldShowPrompt = (newLocation: Location) =>
    showPrompt && newLocation.pathname !== pathname;

  const { addUserRecent, dealId, isMapMode, onChange, onDiscardChanges, onHide } = props;

  useChangeTracking(() => {
    // only setting flag if pathname change assumes closing the panel
    // but if path was changed with another company id to preview
    // we don't set this flag to not call props.onHide in the useEffect below
    if (!PREVIEW_PATH_REGEX.test(pathname)) {
      setPathnameChanged();
    }
  }, [pathname]);

  useEffect(() => {
    if (!showPrompt && isPathnameChanged && !isMapMode) {
      onHide();
    }
  }, [isMapMode, isPathnameChanged, onHide, showPrompt]);

  const handleShowPrompt = useCallback(
    (visible: boolean) => {
      setShowPrompt(visible);
      onChange?.(visible);
    },
    [onChange]
  );

  const handleLeave = useCallback(() => {
    if (isMapMode) {
      setShowPrompt(false);
    } else {
      onChange?.(false);
      onHide();
    }
    onDiscardChanges?.(true);
  }, [isMapMode, onChange, onDiscardChanges, onHide]);

  const handleStay = useCallback(() => {
    onDiscardChanges?.(false);
  }, [onDiscardChanges]);

  const handleSave = useCallback(() => {
    eventEmitter.emit("saveDealChanges");
  }, []);

  useEffect(() => {
    addUserRecent({ entity: { id: dealId, type: EntityType.DEAL } });
  }, [addUserRecent, dealId]);

  const { subscribe, unsubscribe, ...restProps } = props;

  useHideOnDelete(EntityType.DEAL, restProps.dealId, restProps.onHide, subscribe, unsubscribe);

  return (
    <AnalyticsContext.Provider value={dealAnalytics}>
      <HashRouter hashType="noslash">
        <DealRecordPane {...restProps} onShouldShowPrompt={handleShowPrompt} />
      </HashRouter>
      <InterceptNavigationModal
        onLeave={handleLeave}
        onSave={handleSave}
        onStay={handleStay}
        shouldCloseOnLeave
        shouldShowPrompt={shouldShowPrompt}
      />
    </AnalyticsContext.Provider>
  );
};

const mapDispatchToProps = {
  addUserRecent,
  subscribe,
  unsubscribe,
};

export default connect(null, mapDispatchToProps)(RoutedDealRecordPane);
